import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiTwitter, 
  FiLinkedin, 
  FiGithub,
  FiDownload,
  FiExternalLink
} from 'react-icons/fi';

const FooterSection = styled.footer`
  background: rgba(10, 10, 15, 0.8);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 4rem 2rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem 1.5rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const BrandSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #FFFFFF;
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6496FF 0%, #4678FF 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const BrandDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  max-width: 300px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
`;

const ContactIcon = styled.div`
  color: #6496FF;
  font-size: 1.1rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled(motion.a)`
  width: 40px;
  height: 40px;
  background: rgba(100, 150, 255, 0.1);
  border: 1px solid rgba(100, 150, 255, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6496FF;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(100, 150, 255, 0.2);
    border-color: rgba(100, 150, 255, 0.5);
    transform: translateY(-2px);
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ColumnTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #FFFFFF;
  margin-bottom: 0.5rem;
`;

const FooterLink = styled(motion.a)`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.95rem;
  transition: color 0.3s ease;
  cursor: pointer;
  
  &:hover {
    color: #6496FF;
  }
`;

const DownloadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DownloadButton = styled(motion.a)`
  background: linear-gradient(135deg, #6496FF 0%, #4678FF 100%);
  color: #FFFFFF;
  text-decoration: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(100, 150, 255, 0.3);
  }
`;

const SecondaryButton = styled(motion.a)`
  background: rgba(255, 255, 255, 0.1);
  color: #FFFFFF;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const LegalLink = styled(FooterLink)`
  font-size: 0.875rem;
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterSection>
      <Container>
        <FooterContent>
          <BrandSection>
            <Logo>
              <LogoIcon>🧠</LogoIcon>
              Meeting Copilot
            </Logo>
            
            <BrandDescription>
              Your AI teammate for every meeting. Get instant confidence with real-time intelligence 
              that helps you sound smart and stay sharp in any conversation.
            </BrandDescription>
            
            <ContactInfo>
              <ContactItem>
                <ContactIcon><FiMail /></ContactIcon>
                hello@meetingcopilot.com
              </ContactItem>
              <ContactItem>
                <ContactIcon><FiPhone /></ContactIcon>
                +1 (555) 123-4567
              </ContactItem>
              <ContactItem>
                <ContactIcon><FiMapPin /></ContactIcon>
                San Francisco, CA
              </ContactItem>
            </ContactInfo>
            
            <SocialLinks>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiTwitter />
              </SocialLink>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiLinkedin />
              </SocialLink>
              <SocialLink
                href="#"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiGithub />
              </SocialLink>
            </SocialLinks>
          </BrandSection>

          <FooterColumn>
            <ColumnTitle>Product</ColumnTitle>
            <FooterLink whileHover={{ x: 5 }}>Features</FooterLink>
            <FooterLink whileHover={{ x: 5 }}>How it Works</FooterLink>
            <FooterLink whileHover={{ x: 5 }}>Use Cases</FooterLink>
            <FooterLink whileHover={{ x: 5 }}>Pricing</FooterLink>
            <FooterLink whileHover={{ x: 5 }}>Demo</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Support</ColumnTitle>
            <FooterLink whileHover={{ x: 5 }}>Help Center</FooterLink>
            <FooterLink whileHover={{ x: 5 }}>Getting Started</FooterLink>
            <FooterLink whileHover={{ x: 5 }}>Contact Us</FooterLink>
            <FooterLink whileHover={{ x: 5 }}>System Status</FooterLink>
            <FooterLink whileHover={{ x: 5 }}>Community</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Download</ColumnTitle>
            <DownloadSection>
              <DownloadButton
                href="#"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiDownload />
                Get Copilot
              </DownloadButton>
              
              <SecondaryButton
                href="#"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiExternalLink />
                Try Online
              </SecondaryButton>
            </DownloadSection>
            
            <div style={{ marginTop: '1rem' }}>
              <FooterLink whileHover={{ x: 5 }}>macOS</FooterLink>
              <FooterLink whileHover={{ x: 5 }}>Windows</FooterLink>
            </div>
          </FooterColumn>
        </FooterContent>

        <FooterBottom>
          <Copyright>
            © {currentYear} Meeting Copilot. All rights reserved.
          </Copyright>
          
          <LegalLinks>
            <LegalLink whileHover={{ color: '#6496FF' }}>Privacy Policy</LegalLink>
            <LegalLink whileHover={{ color: '#6496FF' }}>Terms of Service</LegalLink>
            <LegalLink whileHover={{ color: '#6496FF' }}>Cookie Policy</LegalLink>
          </LegalLinks>
        </FooterBottom>
      </Container>
    </FooterSection>
  );
};

export default Footer; 